/**
 * NotificationsInbox — popover component with five UI states.
 *
 * Receives `streamState` as a prop — does NOT call `useNotificationStream()`
 * directly (single-subscription-owner invariant per FR-008).
 *
 * Five UI states:
 * 1. Loading — skeleton rows
 * 2. Empty — "You're all caught up"
 * 3. Error — caught by the parent `<ConvexErrorBoundary>`
 * 4. Success — `<NotificationRow>` items in a `<ul role="list">`
 * 5. Partial — success + skeletons while `loadMore` is in flight
 *
 * Keyboard navigation:
 * - Arrow Up/Down move DOM focus across rows (`rowRef.focus()`)
 * - Enter activates (handled by NotificationRow)
 * - Escape closes
 *
 * Focus management:
 * - On mount: focus moves to the heading
 * - On close: focus returns to the bell (via `onClose` prop)
 *
 * @see specs/prd.md Section 3 FR-008
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { NotificationStreamState, InboxItem } from "../../api/types/notification";
import { useMarkNotificationRead, useMarkAllNotificationsRead } from "../../api/hooks";
import { INBOX_PAGE_SIZE } from "../../api/constants/inboxSizing";
import { useHashLocation } from "../../lib/router";
import { NotificationRow } from "./NotificationRow";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface NotificationsInboxProps {
  /** Stream state from the parent `NotificationBellTrigger`. */
  streamState: NotificationStreamState;

  /** Called when the popover should close (Escape, row navigation, etc.). */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const popoverStyle: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  width: "380px",
  maxHeight: "min(480px, 80vh)",
  overflowY: "auto",
  backgroundColor: "var(--color-surface, #fff)",
  border: "1px solid var(--color-border, #e5e7eb)",
  borderRadius: "8px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
  zIndex: 1000,
};

const headerStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 14px",
  borderBottom: "1px solid var(--color-border, #e5e7eb)",
};

const headingStyle: CSSProperties = {
  margin: 0,
  fontSize: "16px",
  fontWeight: 600,
};

const markAllReadBtnStyle: CSSProperties = {
  padding: "4px 10px",
  fontSize: "12px",
  border: "1px solid var(--color-border, #e5e7eb)",
  borderRadius: "4px",
  background: "transparent",
  cursor: "pointer",
  color: "inherit",
};

const emptyStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 14px",
  fontSize: "14px",
  opacity: 0.6,
};

const listStyle: CSSProperties = {
  margin: 0,
  padding: 0,
};

const footerStyle: CSSProperties = {
  padding: "8px 14px",
  borderTop: "1px solid var(--color-border, #e5e7eb)",
  textAlign: "center",
};

const loadMoreBtnStyle: CSSProperties = {
  padding: "6px 14px",
  fontSize: "13px",
  border: "1px solid var(--color-border, #e5e7eb)",
  borderRadius: "4px",
  background: "transparent",
  cursor: "pointer",
  color: "inherit",
  width: "100%",
};

const bannerStyle: CSSProperties = {
  padding: "8px 14px",
  fontSize: "13px",
  color: "var(--color-error, #dc2626)",
  textAlign: "center",
  borderTop: "1px solid var(--color-border, #e5e7eb)",
};

const skeletonRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  padding: "10px 14px",
};

const skeletonDotStyle: CSSProperties = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "var(--color-border, #e5e7eb)",
  flexShrink: 0,
  marginTop: "6px",
};

const skeletonLineStyle: CSSProperties = {
  height: "14px",
  backgroundColor: "var(--color-border, #e5e7eb)",
  borderRadius: "4px",
  marginBottom: "4px",
};

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------

function SkeletonRow({ width }: { width: string }) {
  return (
    <div style={skeletonRowStyle} aria-hidden="true">
      <span style={skeletonDotStyle} />
      <div style={{ flex: 1 }}>
        <div style={{ ...skeletonLineStyle, width }} />
        <div style={{ ...skeletonLineStyle, width: "60%" }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HEADING_ID = "notif-inbox-heading";

export function NotificationsInbox({
  streamState,
  onClose,
}: NotificationsInboxProps): React.ReactElement {
  const [, navigate] = useHashLocation();
  const { action: markRead } = useMarkNotificationRead();
  const { action: markAllRead, isMarking: isMarkingAll } = useMarkAllNotificationsRead();

  const headingRef = useRef<HTMLHeadingElement>(null);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  // -- Focus heading on mount (FR-008 accessibility) -------------------------
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // -- Escape key closes the popover ----------------------------------------
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // -- Row activation (click or Enter) ---------------------------------------
  const handleRowActivate = useCallback(
    (item: InboxItem) => {
      markRead({ notificationId: item.id });

      if (item.navigateHref != null) {
        navigate(item.navigateHref.replace(/^#/, ""));
        onClose();
      }
    },
    [markRead, onClose, navigate],
  );

  // -- Mark all read ---------------------------------------------------------
  const handleMarkAllRead = useCallback(() => {
    markAllRead();
  }, [markAllRead]);

  // -- loadMore with error handling ------------------------------------------
  const handleLoadMore = useCallback(() => {
    if (streamState.status !== "ready") return;

    setLoadMoreError(null);

    // The loadMore in the hook rejects on error.
    // We wrap it to catch and surface the banner.
    try {
      const result = streamState.loadMore();
      // If loadMore returns a promise (it does via internal .catch in the hook),
      // we handle it here for the banner display.
      if (result && typeof (result as Promise<void>).catch === "function") {
        (result as Promise<void>).catch((err: unknown) => {
          const message = err instanceof Error ? err.message : String(err);
          setLoadMoreError(message);
          console.error( // eslint-disable-line no-console
            JSON.stringify({
              event: "notifications.loadMore.error",
              level: "ERROR",
              error_message: message,
              ts_ms: Date.now(),
            }),
          );
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setLoadMoreError(message);
      console.error( // eslint-disable-line no-console
        JSON.stringify({
          event: "notifications.loadMore.error",
          level: "ERROR",
          error_message: message,
          ts_ms: Date.now(),
        }),
      );
    }
  }, [streamState]);

  // -- Arrow key navigation --------------------------------------------------
  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLUListElement>) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

      e.preventDefault();
      const current = rowRefs.current.findIndex(
        (ref) => ref === document.activeElement,
      );

      let next: number;
      if (e.key === "ArrowDown") {
        next = current < 0 ? 0 : Math.min(current + 1, rowRefs.current.length - 1);
      } else {
        next = current <= 0 ? 0 : current - 1;
      }

      rowRefs.current[next]?.focus();
    },
    [],
  );

  // -- Derive items and metadata from stream state ---------------------------
  const isLoading = streamState.status === "loading";
  const isEmpty = streamState.status === "empty";
  const items = streamState.status === "ready" ? streamState.data.items : [];
  const unreadCount = streamState.status === "ready" ? streamState.data.unreadCount : 0;
  const hasMore = streamState.status === "ready" ? streamState.data.hasMore : false;
  const isLoadingMore = streamState.status === "ready" ? streamState.data.isLoadingMore : false;

  // Sync row refs array length with items
  rowRefs.current = rowRefs.current.slice(0, items.length);

  return (
    <div
      role="dialog"
      aria-labelledby={HEADING_ID}
      style={popoverStyle}
      data-testid="notifications-inbox"
    >
      {/* Header */}
      <div style={headerStyle}>
        <h2
          id={HEADING_ID}
          ref={headingRef}
          tabIndex={-1}
          style={headingStyle}
        >
          Notifications
        </h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={isMarkingAll}
            style={markAllReadBtnStyle}
            data-testid="mark-all-read-btn"
          >
            {isMarkingAll ? "Marking..." : "Mark all read"}
          </button>
        )}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div data-testid="inbox-loading">
          <SkeletonRow width="80%" />
          <SkeletonRow width="70%" />
          <SkeletonRow width="75%" />
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div style={emptyStyle} data-testid="inbox-empty">
          You&apos;re all caught up
        </div>
      )}

      {/* Success / partial state */}
      {items.length > 0 && (
        <ul
          role="list"
          style={listStyle}
          onKeyDown={handleListKeyDown}
          data-testid="notification-list"
        >
          {items.map((item, index) => (
            <NotificationRow
              key={item.id}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              item={item}
              onActivate={handleRowActivate}
            />
          ))}
        </ul>
      )}

      {/* Loading more skeletons (partial state) */}
      {isLoadingMore && (
        <div data-testid="inbox-loading-more">
          <SkeletonRow width="75%" />
          <SkeletonRow width="65%" />
        </div>
      )}

      {/* Load more button */}
      {hasMore && !isLoadingMore && (
        <div style={footerStyle}>
          <button
            type="button"
            onClick={handleLoadMore}
            style={loadMoreBtnStyle}
            data-testid="load-more-btn"
          >
            {`Load ${INBOX_PAGE_SIZE} more`}
          </button>
        </div>
      )}

      {/* loadMore error banner */}
      {loadMoreError != null && (
        <div style={bannerStyle} data-testid="load-more-error">
          Couldn&apos;t load more notifications. Try again.
        </div>
      )}
    </div>
  );
}
